"use client";

import React, { JSX, useRef } from "react";
import {
  motion,
  useAnimationFrame,
  useMotionTemplate,
  useMotionValue,
  useTransform,
} from "framer-motion";
import { cn } from "@/lib/utils";
import { InteractiveButton } from "../core/button/InteractiveButton";


type CTAButtonProps = {
  text: string;
  href?: string;
  tooltip?: string;
  onClickAction?: () => void;
  borderRadius?: string;
  containerClassName?: string;
  borderClassName?: string;
  duration?: number;
  className?: string;
  animate?: boolean;
};

export function CTAButton({
  text,
  href,
  tooltip,
  onClickAction,
  borderRadius = "1.75rem",
  containerClassName = "",
  borderClassName = "",
  duration = 2000,
  className = "",
  animate = true,
}: CTAButtonProps) {
  return (
    <div
      title={tooltip || ""}
      className={cn(
        "relative text-xl h-12 w-full p-[2px] overflow-hidden hover:opacity-90",
        containerClassName
      )}
      style={{ borderRadius }}
    >
      <div
        className="absolute inset-0"
        style={{ borderRadius: `calc(${borderRadius} * 0.96)` }}
      >
        <MovingBorder duration={duration} rx="30%" ry="30%">
          <div
            className={cn(
              "h-20 w-20 opacity-[0.8] p-2 bg-[radial-gradient(var(--purple-400)_40%,transparent_60%)]",
              borderClassName
            )}
          />
        </MovingBorder>
      </div>

      <InteractiveButton
        href={href ?? "#"}
        label={text}
        onClick={onClickAction}
        animate={animate}
        isStatic={true}
        variant="ghost"
        className={cn(
          "relative bg-card/90/[0.8] p-2 border border-slate-800 backdrop-blur-xl text-card-foreground flex items-center justify-center w-full h-full text-sm antialiased",
          className
        )}
        style={{ borderRadius: `calc(${borderRadius} * 0.96)` }}
      />
    </div>
  );
}

type MovingBorderProps = {
  children: React.ReactNode;
  duration: number;
  rx: string;
  ry: string;
  [key: string]: any;
};

export const MovingBorder = ({
  children,
  duration,
  rx,
  ry,
  ...otherProps
}: MovingBorderProps) => {
  const pathRef = useRef<SVGRectElement>(null);
  const progress = useMotionValue(0);

  useAnimationFrame((time) => {
    const length = pathRef.current?.getTotalLength?.();
    if (length) {
      const pxPerMillisecond = length / duration;
      progress.set((time * pxPerMillisecond) % length);
    }
  });

  const x = useTransform(
    progress,
    (val) => pathRef.current?.getPointAtLength(val)?.x ?? 0
  );
  const y = useTransform(
    progress,
    (val) => pathRef.current?.getPointAtLength(val)?.y ?? 0
  );

  const transform = useMotionTemplate`translateX(${x}px) translateY(${y}px) translateX(-50%) translateY(-50%)`;

  return (
    <>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        preserveAspectRatio="none"
        className="absolute h-full w-full"
        width="100%"
        height="100%"
        {...otherProps}
      >
        <rect
          fill="none"
          width="100%"
          height="100%"
          rx={rx}
          ry={ry}
          ref={pathRef}
        />
      </svg>
      <motion.div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          display: "inline-block",
          transform,
        }}
      >
        {children}
      </motion.div>
    </>
  );
};
